import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';
import { PLATFORM_COLLECTIONS } from './platformSchema';
import { getThreadNavigationTarget } from './threadNavigation';
import { createClientNote } from './clientCRM';
import { isDeliveryBearing } from './lessonHandoff';

function normalizeString(value = '') {
  return String(value || '').trim();
}

function toDateValue(value) {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (value?.seconds) return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function toTimestampValue(value) {
  return toDateValue(value)?.getTime?.() || 0;
}

// Pass 80 — a retention action is clientId-NATIVE, EXPLICIT operator event history (append-only, immutable). Allowed
// actionTypes describe things an operator ACTUALLY did — never "sent" for a mere draft.
export const RETENTION_ACTION_TYPES = new Set([
  'reviewed', 'snoozed', 'rebook_request_created', 'followup_draft_prepared', 'package_offer_draft_prepared',
  'note_added', 'followup_sent', 'package_offered', 'rebook_suggested', 'instructor_reassigned',
]);

export function normalizeRetentionAction(record = {}, fallbackId = '') {
  const id = record.id || record.retentionActionId || fallbackId || '';
  return {
    id,
    retentionActionId: id,
    organizationId: record.organizationId || '',
    // Pass 80 — `clientId` is the CANONICAL commercial identity; `clientUserId` is an OPTIONAL Auth link only.
    clientId: record.clientId || '',
    clientUserId: record.clientUserId || '',
    instructorId: record.instructorId || '',
    actionType: normalizeString(record.actionType || ''),
    status: normalizeString(record.status || 'logged'),
    queueType: normalizeString(record.queueType || ''),
    relatedBookingId: record.relatedBookingId || '',
    relatedBookingRequestId: record.relatedBookingRequestId || '',
    relatedServiceId: record.relatedServiceId || '',
    relatedThreadId: record.relatedThreadId || '',
    templateKey: normalizeString(record.templateKey || ''),
    templateId: record.templateId || '',
    note: normalizeString(record.note || ''),
    snoozedUntil: record.snoozedUntil || '',
    createdAt: record.createdAt || null,
    createdByUserId: record.createdByUserId || '',
  };
}

export async function logRetentionAction(
  db,
  {
    organizationId = '',
    clientId = '',
    clientUserId = '',
    instructorId = '',
    actionType = '',
    queueType = '',
    relatedBookingId = '',
    relatedBookingRequestId = '',
    relatedServiceId = '',
    relatedThreadId = '',
    templateKey = '',
    templateId = '',
    note = '',
    status = 'logged',
    snoozedUntil = '',
    createdByUserId = '',
  } = {}
) {
  // Pass 80 — a canonical action requires the commercial clientId + actor; clientUserId is optional (Auth link).
  if (!organizationId || !clientId || !actionType || !createdByUserId) {
    throw new Error('organizationId, clientId, actionType, and createdByUserId are required.');
  }
  if (!RETENTION_ACTION_TYPES.has(actionType)) {
    throw new Error(`Unknown retention actionType: ${actionType}`);
  }

  const normalized = normalizeRetentionAction({
    organizationId, clientId, clientUserId, instructorId, actionType, queueType,
    relatedBookingId, relatedBookingRequestId, relatedServiceId, relatedThreadId,
    templateKey, templateId, note, status, snoozedUntil, createdByUserId,
  });

  const docRef = await addDoc(collection(db, PLATFORM_COLLECTIONS.RETENTION_ACTIONS), {
    ...normalized,
    createdAt: serverTimestamp(),
  });

  return normalizeRetentionAction(normalized, docRef?.id || '');
}

// Read the canonical clientId-native actions MERGED with legacy clientUserId actions (Auth-backed), deduped, org-scoped.
export async function getClientRetentionActions(
  db,
  {
    organizationId = '',
    clientId = '',
    clientUserId = '',
  } = {}
) {
  if (!organizationId || (!clientId && !clientUserId)) return [];
  const [modernSnap, legacySnap] = await Promise.all([
    clientId ? getDocs(query(collection(db, PLATFORM_COLLECTIONS.RETENTION_ACTIONS), where('clientId', '==', clientId))) : Promise.resolve({ docs: [] }),
    clientUserId ? getDocs(query(collection(db, PLATFORM_COLLECTIONS.RETENTION_ACTIONS), where('clientUserId', '==', clientUserId))) : Promise.resolve({ docs: [] }),
  ]);
  const seen = new Set();
  return [...(modernSnap.docs || []), ...(legacySnap.docs || [])]
    .map((docSnap) => normalizeRetentionAction({ id: docSnap.id, ...docSnap.data() }))
    .filter((action) => {
      if (action.organizationId !== organizationId) return false;
      const aid = action.retentionActionId || action.id;
      if (!aid || seen.has(aid)) return false;
      seen.add(aid);
      return true;
    })
    .sort((left, right) => toTimestampValue(right.createdAt) - toTimestampValue(left.createdAt));
}

export async function getOrganizationRetentionActions(db, organizationId = '') {
  if (!organizationId) return [];
  const snapshot = await getDocs(
    query(
      collection(db, PLATFORM_COLLECTIONS.RETENTION_ACTIONS),
      where('organizationId', '==', organizationId)
    )
  );

  return (snapshot.docs || [])
    .map((docSnap) => normalizeRetentionAction({ id: docSnap.id, ...docSnap.data() }))
    .sort((left, right) => toTimestampValue(right.createdAt) - toTimestampValue(left.createdAt));
}

export function getFollowUpHistoryForClient(actions = []) {
  return (actions || []).filter((action) => ['followup_sent', 'package_offered', 'rebook_suggested'].includes(action.actionType));
}

export function getRetentionActionSummary(actions = []) {
  const summary = (actions || []).reduce((acc, action) => {
    const type = action.actionType || 'unknown';
    acc.totalCount += 1;
    acc.byType[type] = (acc.byType[type] || 0) + 1;
    return acc;
  }, {
    totalCount: 0,
    byType: {},
  });

  return {
    ...summary,
    followUpSentCount: summary.byType.followup_sent || 0,
    packageOfferedCount: summary.byType.package_offered || 0,
    rebookSuggestedCount: summary.byType.rebook_suggested || 0,
    snoozedCount: summary.byType.snoozed || 0,
    reviewedCount: summary.byType.reviewed || 0,
  };
}

export function buildFollowUpDraftMessage(
  clientProfile = {},
  {
    recommendations = {},
    coachingSummary = '',
  } = {}
) {
  const displayName = normalizeString(clientProfile.displayName || clientProfile.firstName || 'there');
  const baseMessage = normalizeString(recommendations.followUpMessage || `Hi ${displayName}, when you're ready, a follow-up session would be a strong next step.`);
  const coachingDetail = normalizeString(coachingSummary || '');
  return coachingDetail ? `${baseMessage} ${coachingDetail}` : baseMessage;
}

export function buildPackageOfferDraftMessage(
  clientProfile = {},
  {
    recommendations = {},
    coachingSummary = '',
  } = {}
) {
  const displayName = normalizeString(clientProfile.displayName || clientProfile.firstName || 'there');
  const packageName = normalizeString(recommendations.packageSuggestion?.name || 'a coaching package');
  const nextServiceName = normalizeString(recommendations.nextService?.name || 'the next session');
  const summary = normalizeString(coachingSummary || '');
  return `Hi ${displayName}, ${packageName} could be a strong fit if you want to keep building momentum through ${nextServiceName}.${summary ? ` ${summary}` : ''}`;
}

function appendTemplateVars(searchParams, templateVars = {}) {
  Object.entries(templateVars || {}).forEach(([key, value]) => {
    if (!value) return;
    searchParams.set(key, String(value));
  });
}

function buildRetentionThreadId({
  organizationId = '',
  senderUserId = '',
  clientUserId = '',
} = {}) {
  if (!senderUserId || !clientUserId) return '';
  return `retention_${organizationId || 'org'}_${senderUserId}_${clientUserId}`;
}

export async function openThreadWithDraft(
  db,
  {
    organizationId = '',
    clientProfile = {},
    senderUserId = '',
    senderName = '',
    draftMessage = '',
    draftType = 'followup_sent',
    templateKey = '',
    templateVars = {},
    relatedServiceId = '',
    relatedBookingId = '',
  } = {}
) {
  const clientUserId = clientProfile.userId || '';
  if (!senderUserId || !clientUserId) {
    throw new Error('senderUserId and client user are required.');
  }

  const threadId = buildRetentionThreadId({
    organizationId,
    senderUserId,
    clientUserId,
  });
  const threadRef = doc(db, 'threads', threadId);
  const threadSnap = await getDoc(threadRef);

  const baseData = {
    participantIds: [senderUserId, clientUserId],
    studentId: clientUserId,
    clientUserId,
    instructorId: senderUserId,
    organizationId,
    relatedBookingId: relatedBookingId || '',
    relatedServiceId: relatedServiceId || '',
    title: `${normalizeString(clientProfile.displayName || clientProfile.email || 'Client')} Follow-Up`,
    instructorName: senderName || 'Coach',
  };

  await setDoc(
    threadRef,
    threadSnap.exists()
      ? baseData
      : {
          ...baseData,
          lastMessage: 'Draft follow-up ready to review before sending.',
          lastMessageTimestamp: serverTimestamp(),
        },
    { merge: true }
  );

  const searchParams = new URLSearchParams({
    tab: 'messages',
    thread: threadId,
    draft: draftMessage,
    retentionActionType: draftType,
    retentionOrgId: organizationId,
    retentionClientUserId: clientUserId,
  });

  if (templateKey) searchParams.set('templateKey', templateKey);
  if (relatedServiceId) searchParams.set('relatedServiceId', relatedServiceId);
  if (relatedBookingId) searchParams.set('relatedBookingId', relatedBookingId);
  appendTemplateVars(searchParams, templateVars);

  return {
    threadId,
    link: `${getThreadNavigationTarget(threadId)}&${searchParams.toString().replace(/^tab=messages&thread=[^&]+&?/, '')}`,
  };
}

export function getRetentionActionsByType(actions = [], actionType = '') {
  return (actions || []).filter((action) => !actionType || action.actionType === actionType);
}

export function getPublicServiceToBookingConversion(services = [], bookingRequests = [], bookings = []) {
  return (services || []).map((service) => {
    const serviceId = service.serviceId || service.id || '';
    const relatedRequests = (bookingRequests || []).filter((request) => (request.serviceId || '') === serviceId);
    const relatedBookings = (bookings || []).filter((booking) => (booking.serviceId || '') === serviceId);
    return {
      serviceId,
      serviceName: service.name || 'Service',
      ...getPublicToBookingConversion(relatedRequests, relatedBookings),
    };
  });
}

export function getLessonToPackageConversion(lessons = [], memberships = []) {
  const lessonUserIds = new Set(
    (lessons || []).filter(isDeliveryBearing)
      .map((lesson) => lesson.studentId || lesson.userId || lesson.clientUserId || '')
      .filter(Boolean)
  );
  const convertedCount = (memberships || []).filter((membership) => lessonUserIds.has(membership.userId)).length;
  return {
    completedLessonClientCount: lessonUserIds.size,
    packagePurchaseCount: convertedCount,
    conversionRate: lessonUserIds.size > 0 ? Number((convertedCount / lessonUserIds.size).toFixed(2)) : 0,
  };
}

export function getRetentionActionAttributionSummary(
  actions = [],
  {
    bookings = [],
    memberships = [],
  } = {}
) {
  return {
    followUpToRebook: getFollowUpToRebookConversion(actions, bookings),
    packageOfferToPurchase: getPackageOfferToPurchaseConversion(actions, memberships),
  };
}

export function filterRetentionQueue(entries = [], filters = {}) {
  return (entries || []).filter((entry) => {
    const profile = entry.profile || {};
    const recommendations = entry.recommendations || {};
    const risk = entry.risk || {};
    const signals = entry.riskSignals || {};
    const memberships = entry.memberships || [];
    const latestBooking = entry.latestBooking || {};

    if (filters.instructorId && (latestBooking.instructorId || '') !== filters.instructorId) return false;
    if (filters.serviceId && ((latestBooking.serviceId || recommendations.nextService?.serviceId || recommendations.nextService?.id || '') !== filters.serviceId)) return false;
    if (filters.riskLevel && (risk.level || '') !== filters.riskLevel) return false;
    if (filters.discipline && ((profile.primaryDiscipline || recommendations.nextService?.discipline || '') !== filters.discipline)) return false;
    if (filters.locationType && ((recommendations.nextService?.locationType || '') !== filters.locationType)) return false;

    if (filters.packageStatus === 'has_balance') {
      if (!((signals.remainingSessions || 0) > 0 || (signals.remainingCredits || 0) > 0)) return false;
    }
    if (filters.packageStatus === 'none' && memberships.length > 0) return false;
    if (filters.membershipStatus && filters.membershipStatus !== 'any') {
      if (!memberships.some((membership) => (membership.status || '') === filters.membershipStatus)) return false;
    }
    if (filters.lastActivityWindow) {
      const minimumDays = Number(filters.lastActivityWindow);
      if (Number.isFinite(minimumDays) && (signals.daysSinceLastActivity || 0) < minimumDays) return false;
    }

    return true;
  });
}

export function getRetentionQueueFilterOptions(entries = []) {
  const instructorIds = new Set();
  const services = new Map();
  const disciplines = new Set();
  const locationTypes = new Set();
  const membershipStatuses = new Set();

  (entries || []).forEach((entry) => {
    const latestBooking = entry.latestBooking || {};
    const recommendations = entry.recommendations || {};
    const profile = entry.profile || {};
    const memberships = entry.memberships || [];
    if (latestBooking.instructorId) instructorIds.add(latestBooking.instructorId);
    const service = recommendations.nextService || {};
    const serviceId = latestBooking.serviceId || service.serviceId || service.id || '';
    const serviceName = latestBooking.serviceNameSnapshot || service.name || '';
    if (serviceId && serviceName) services.set(serviceId, serviceName);
    if (profile.primaryDiscipline || service.discipline) disciplines.add(profile.primaryDiscipline || service.discipline);
    if (service.locationType) locationTypes.add(service.locationType);
    memberships.forEach((membership) => {
      if (membership.status) membershipStatuses.add(membership.status);
    });
  });

  return {
    instructorIds: Array.from(instructorIds),
    services: Array.from(services.entries()).map(([value, label]) => ({ value, label })),
    disciplines: Array.from(disciplines),
    locationTypes: Array.from(locationTypes),
    membershipStatuses: Array.from(membershipStatuses),
    riskLevels: ['high', 'medium', 'low'],
    activityWindows: ['14', '30', '60'],
    packageStatuses: ['has_balance', 'none'],
  };
}

export function getPublicToBookingConversion(bookingRequests = [], bookings = []) {
  const publicRequests = (bookingRequests || []).filter((request) => String(request.sourceType || '').startsWith('public'));
  const confirmedFromPublic = (bookings || []).filter((booking) =>
    publicRequests.some((request) => (request.bookingRequestId || request.id) === (booking.bookingRequestId || ''))
  );
  return {
    publicRequestCount: publicRequests.length,
    publicConfirmedCount: confirmedFromPublic.length,
    conversionRate: publicRequests.length > 0 ? Number((confirmedFromPublic.length / publicRequests.length).toFixed(2)) : 0,
  };
}

export function getBookingToLessonConversion(bookings = [], lessons = []) {
  const completedLessons = (lessons || []).filter(isDeliveryBearing); // Pass 80 §45 — delivered = delivery-bearing
  const linkedBookings = (bookings || []).filter((booking) =>
    booking.lessonId || completedLessons.some((lesson) => (lesson.bookingId || '') === (booking.bookingId || booking.id || ''))
  );
  return {
    bookingCount: (bookings || []).length,
    lessonLinkedCount: linkedBookings.length,
    conversionRate: (bookings || []).length > 0 ? Number((linkedBookings.length / bookings.length).toFixed(2)) : 0,
  };
}

export function getLessonToRebookConversion(bookings = [], lessons = []) {
  const completedLessons = (lessons || []).filter(isDeliveryBearing); // Pass 80 §45 — delivered = delivery-bearing
  const rebookedUserIds = new Set(
    (bookings || [])
      .filter((booking) => String(booking.sourceType || '') === 'client_profile_rebook')
      .map((booking) => booking.requesterUserId)
      .filter(Boolean)
  );
  const completedUserIds = new Set(
    completedLessons
      .map((lesson) => lesson.studentId || lesson.userId || lesson.clientUserId || '')
      .filter(Boolean)
  );
  const rebookedAfterLessonCount = Array.from(completedUserIds).filter((userId) => rebookedUserIds.has(userId)).length;
  return {
    completedLessonClientCount: completedUserIds.size,
    rebookedAfterLessonCount,
    conversionRate: completedUserIds.size > 0 ? Number((rebookedAfterLessonCount / completedUserIds.size).toFixed(2)) : 0,
  };
}

export function getFollowUpToRebookConversion(actions = [], bookings = []) {
  const followUps = (actions || []).filter((action) => action.actionType === 'followup_sent');
  const rebookedCount = followUps.filter((action) =>
    (bookings || []).some((booking) =>
      booking.requesterUserId === action.clientUserId &&
      toTimestampValue(booking.createdAt || booking.startAt) >= toTimestampValue(action.createdAt)
    )
  ).length;
  return {
    followUpCount: followUps.length,
    rebookedAfterFollowUpCount: rebookedCount,
    conversionRate: followUps.length > 0 ? Number((rebookedCount / followUps.length).toFixed(2)) : 0,
  };
}

export function getFollowUpConversionRate(actions = [], bookings = []) {
  const summary = getFollowUpToRebookConversion(actions, bookings);
  return {
    followUpSentCount: summary.followUpCount,
    rebookedAfterFollowUpCount: summary.rebookedAfterFollowUpCount,
    conversionRate: summary.conversionRate,
  };
}

export function getPackageOfferToPurchaseConversion(actions = [], memberships = []) {
  const offers = (actions || []).filter((action) => action.actionType === 'package_offered');
  const convertedCount = offers.filter((action) =>
    (memberships || []).some((membership) =>
      membership.userId === action.clientUserId &&
      toTimestampValue(membership.createdAt || membership.startAt) >= toTimestampValue(action.createdAt)
    )
  ).length;
  return {
    offerCount: offers.length,
    convertedCount,
    conversionRate: offers.length > 0 ? Number((convertedCount / offers.length).toFixed(2)) : 0,
  };
}

export function getPackageOfferConversionRate(actions = [], memberships = []) {
  const summary = getPackageOfferToPurchaseConversion(actions, memberships);
  return {
    packageOfferCount: summary.offerCount,
    purchasesAfterOfferCount: summary.convertedCount,
    conversionRate: summary.conversionRate,
  };
}

export function getRebookAfterLessonRate(bookings = [], lessons = []) {
  const summary = getLessonToRebookConversion(bookings, lessons);
  return {
    completedLessonClientCount: summary.completedLessonClientCount,
    rebookedAfterLessonCount: summary.rebookedAfterLessonCount,
    conversionRate: summary.conversionRate,
  };
}

export function getRetentionRevenueSummary(
  actions = [],
  {
    bookings = [],
    bookingPayments = [],
  } = {}
) {
  const attributableActionClientIds = new Set(
    (actions || [])
      .filter((action) => ['followup_sent', 'package_offered', 'rebook_suggested'].includes(action.actionType))
      .map((action) => action.clientUserId)
      .filter(Boolean)
  );

  const attributableBookings = (bookings || []).filter((booking) => attributableActionClientIds.has(booking.requesterUserId));
  const attributablePayments = (bookingPayments || []).filter((payment) =>
    attributableBookings.some((booking) => (booking.bookingId || booking.id) === (payment.bookingId || ''))
  );
  const bookingRevenue = attributableBookings.reduce((total, booking) => total + Number(booking.priceSnapshot || booking.servicePricingSnapshot || 0), 0);
  const paidRevenue = attributablePayments.reduce((total, payment) => (
    ['paid', 'authorized', 'partially_paid'].includes(payment.status)
      ? total + Number(payment.amount || 0)
      : total
  ), 0);

  return {
    attributableBookingCount: attributableBookings.length,
    attributableRevenue: paidRevenue || bookingRevenue,
  };
}

export function getRetentionConversionSummary(
  actions = [],
  {
    bookings = [],
    lessons = [],
    memberships = [],
    bookingPayments = [],
  } = {}
) {
  return {
    followUp: getFollowUpConversionRate(actions, bookings),
    packageOffer: getPackageOfferConversionRate(actions, memberships),
    rebookAfterLesson: getRebookAfterLessonRate(bookings, lessons),
    revenue: getRetentionRevenueSummary(actions, {
      bookings,
      bookingPayments,
    }),
  };
}

export function getConversionByTrafficSource(bookingRequests = [], bookings = []) {
  const grouped = (bookingRequests || []).reduce((acc, request) => {
    const sourceType = request.sourceType || 'unknown';
    if (!acc[sourceType]) acc[sourceType] = [];
    acc[sourceType].push(request);
    return acc;
  }, {});

  return Object.entries(grouped).map(([sourceType, requests]) => ({
    sourceType,
    ...getPublicToBookingConversion(requests, bookings.filter((booking) =>
      requests.some((request) => (request.bookingRequestId || request.id) === (booking.bookingRequestId || ''))
    )),
  }));
}

export function getFullConversionFunnel(
  {
    bookingRequests = [],
    bookings = [],
    lessons = [],
    actions = [],
    memberships = [],
  } = {}
) {
  return {
    publicToBooking: getPublicToBookingConversion(bookingRequests, bookings),
    bookingToLesson: getBookingToLessonConversion(bookings, lessons),
    lessonToRebook: getLessonToRebookConversion(bookings, lessons),
    followUpToRebook: getFollowUpToRebookConversion(actions, bookings),
    packageOfferToPurchase: getPackageOfferToPurchaseConversion(actions, memberships),
  };
}

export function getConversionByService(services = [], { bookingRequests = [], bookings = [], lessons = [], actions = [], memberships = [] } = {}) {
  return (services || []).map((service) => {
    const serviceId = service.serviceId || service.id || '';
    const relatedRequests = (bookingRequests || []).filter((request) => (request.serviceId || '') === serviceId);
    const relatedBookings = (bookings || []).filter((booking) => (booking.serviceId || '') === serviceId);
    const relatedLessons = (lessons || []).filter((lesson) => (lesson.serviceId || '') === serviceId);
    const relatedActions = (actions || []).filter((action) => (action.relatedServiceId || '') === serviceId);
    const publicToBooking = getPublicToBookingConversion(relatedRequests, relatedBookings);
    const bookingToLesson = getBookingToLessonConversion(relatedBookings, relatedLessons);
    const lessonToPackage = getLessonToPackageConversion(relatedLessons, memberships.filter((membership) =>
      relatedLessons.some((lesson) => (lesson.studentId || lesson.userId || lesson.clientUserId || '') === membership.userId)
    ));
    const followUpToRebook = getFollowUpToRebookConversion(relatedActions, relatedBookings);
    const packageOfferToPurchase = getPackageOfferToPurchaseConversion(relatedActions, memberships);
    return {
      serviceId,
      serviceName: service.name || 'Service',
      ...publicToBooking,
      ...bookingToLesson,
      ...lessonToPackage,
      ...followUpToRebook,
      ...packageOfferToPurchase,
      publicToBookingRate: publicToBooking.conversionRate,
      bookingToLessonRate: bookingToLesson.conversionRate,
      lessonToPackageRate: lessonToPackage.conversionRate,
      followUpToRebookRate: followUpToRebook.conversionRate,
      packageOfferToPurchaseRate: packageOfferToPurchase.conversionRate,
      conversionRate: publicToBooking.conversionRate,
    };
  });
}

export function getConversionByInstructor(instructorId = '', { bookingRequests = [], bookings = [], lessons = [], actions = [], memberships = [] } = {}) {
  const filteredRequests = (bookingRequests || []).filter((request) => !instructorId || request.instructorId === instructorId);
  const filteredBookings = (bookings || []).filter((booking) => !instructorId || booking.instructorId === instructorId);
  const filteredLessons = (lessons || []).filter((lesson) => !instructorId || lesson.instructorId === instructorId);
  const filteredActions = (actions || []).filter((action) => !instructorId || action.instructorId === instructorId);
  const publicToBooking = getPublicToBookingConversion(filteredRequests, filteredBookings);
  const bookingToLesson = getBookingToLessonConversion(filteredBookings, filteredLessons);
  const lessonToPackage = getLessonToPackageConversion(filteredLessons, memberships);
  const followUpToRebook = getFollowUpToRebookConversion(filteredActions, filteredBookings);
  const packageOfferToPurchase = getPackageOfferToPurchaseConversion(filteredActions, memberships);
  return {
    instructorId,
    ...publicToBooking,
    ...bookingToLesson,
    ...lessonToPackage,
    ...followUpToRebook,
    ...packageOfferToPurchase,
    publicToBookingRate: publicToBooking.conversionRate,
    bookingToLessonRate: bookingToLesson.conversionRate,
    lessonToPackageRate: lessonToPackage.conversionRate,
    followUpToRebookRate: followUpToRebook.conversionRate,
    packageOfferToPurchaseRate: packageOfferToPurchase.conversionRate,
    conversionRate: publicToBooking.conversionRate,
  };
}

export async function performBulkRetentionAction(entries = [], { handler } = {}) {
  const validEntries = (entries || []).filter(Boolean);
  if (typeof handler !== 'function') {
    throw new Error('A handler function is required for bulk retention actions.');
  }
  const results = [];
  for (const entry of validEntries) {
    // Keep this sequential so queue actions stay predictable and easy to audit.
    // This is intentionally operational rather than high-throughput automation.
    // eslint-disable-next-line no-await-in-loop
    results.push(await handler(entry));
  }
  return results;
}

export function buildBulkFollowUpDrafts(entries = [], { buildDraft } = {}) {
  return (entries || []).map((entry) => ({
    clientUserId: entry?.profile?.userId || '',
    draftMessage: typeof buildDraft === 'function' ? buildDraft(entry) : '',
    relatedServiceId: entry?.recommendations?.nextService?.serviceId || entry?.recommendations?.nextService?.id || '',
    relatedBookingId: entry?.latestBooking?.bookingId || entry?.latestBooking?.id || '',
  })).filter((entry) => entry.clientUserId);
}

// Pass 80 — entries are retention OPPORTUNITIES (clientId-native). `entry.clientId` is required; the paired
// clientProfiles dual-write is REMOVED — retentionActions is the ONE canonical review/snooze state (derived on read).
const entryClientId = (entry) => entry?.clientId || entry?.profile?.clientId || '';
const entryClientUserId = (entry) => entry?.clientUserId || entry?.profile?.userId || '';
const entryServiceId = (entry) => entry?.suggestedService?.serviceId || entry?.latestBooking?.serviceId || '';
const entryBookingId = (entry) => entry?.lastDeliveredService?.bookingId || entry?.latestBooking?.bookingId || '';

export async function bulkSnoozeClients(
  db,
  {
    organizationId = '',
    entries = [],
    snoozedByUserId = '',
    queueType = '',
    until = '',
  } = {}
) {
  return performBulkRetentionAction(entries, {
    handler: async (entry) => logRetentionAction(db, {
      organizationId,
      clientId: entryClientId(entry),
      clientUserId: entryClientUserId(entry),
      instructorId: snoozedByUserId,
      actionType: 'snoozed',
      queueType,
      snoozedUntil: until,
      relatedServiceId: entryServiceId(entry),
      relatedBookingId: entryBookingId(entry),
      note: `Snoozed from ${queueType || 'retention'} queue.`,
      createdByUserId: snoozedByUserId,
    }),
  });
}

export async function bulkAssignInstructor(
  db,
  {
    organizationId = '',
    entries = [],
    instructorId = '',
    assignedByUserId = '',
  } = {}
) {
  return performBulkRetentionAction(entries, {
    handler: async (entry) => logRetentionAction(db, {
      organizationId,
      clientId: entryClientId(entry),
      clientUserId: entryClientUserId(entry),
      instructorId,
      actionType: 'instructor_reassigned',
      relatedServiceId: entryServiceId(entry),
      relatedBookingId: entryBookingId(entry),
      note: `Reassigned to instructor ${instructorId}.`,
      createdByUserId: assignedByUserId || instructorId,
    }),
  });
}

export async function bulkAddClientNote(
  db,
  {
    organizationId = '',
    entries = [],
    createdByUserId = '',
    instructorId = '',
    noteText = '',
    visibility = 'org',
  } = {}
) {
  if (!normalizeString(noteText)) {
    throw new Error('noteText is required for bulk note entry.');
  }

  return performBulkRetentionAction(entries, {
    handler: async (entry) => {
      await createClientNote(db, {
        organizationId,
        clientId: entryClientId(entry),
        clientUserId: entryClientUserId(entry),
        createdByAuthUserId: createdByUserId || instructorId,
        instructorId,
        noteText,
        relatedBookingId: entryBookingId(entry),
        visibility,
      });
      return logRetentionAction(db, {
        organizationId,
        clientId: entryClientId(entry),
        clientUserId: entryClientUserId(entry),
        instructorId,
        actionType: 'note_added',
        relatedServiceId: entryServiceId(entry),
        relatedBookingId: entryBookingId(entry),
        note: noteText,
        createdByUserId,
      });
    },
  });
}

export function getBulkActionPreview(entries = [], { actionType = '' } = {}) {
  const queueTypes = Array.from(new Set((entries || []).map((entry) => entry?.queueType).filter(Boolean)));
  const serviceIds = Array.from(new Set((entries || []).map((entry) => entry?.latestBooking?.serviceId || entry?.recommendations?.nextService?.serviceId).filter(Boolean)));
  return {
    actionType,
    clientCount: (entries || []).length,
    queueTypes,
    serviceIds,
  };
}

export async function bulkCreatePackageOfferSuggestion(entries = []) {
  return (entries || []).map((entry) => ({
    clientUserId: entry?.profile?.userId || '',
    packageName: entry?.recommendations?.packageSuggestion?.name || '',
    suggestedServiceName: entry?.recommendations?.nextService?.name || '',
  })).filter((entry) => entry.clientUserId);
}

export async function bulkMarkReviewed(
  db,
  {
    organizationId = '',
    entries = [],
    reviewedByUserId = '',
    queueType = '',
  } = {}
) {
  return performBulkRetentionAction(entries, {
    handler: async (entry) => logRetentionAction(db, {
      organizationId,
      clientId: entryClientId(entry),
      clientUserId: entryClientUserId(entry),
      instructorId: reviewedByUserId,
      actionType: 'reviewed',
      queueType,
      relatedServiceId: entryServiceId(entry),
      relatedBookingId: entryBookingId(entry),
      note: `Reviewed from ${queueType || 'retention'} queue.`,
      createdByUserId: reviewedByUserId,
    }),
  });
}
